/**
 * Shared types for @jumpstone/spreadsheet.
 *
 * The package is product-agnostic: nothing here knows about any specific
 * domain (no "invoice", "employee", "ledger" — a CMS or a monitoring tool
 * could consume it unchanged). A "sheet" is just a named grid of strings.
 */

/** Stable error codes a {@link SpreadsheetError} can carry. */
export type SpreadsheetErrorCode =
  /** The bytes are not a ZIP container at all (no end-of-central-directory). */
  | 'NOT_A_ZIP'
  /** A ZIP structure (central dir / local header) is malformed. */
  | 'MALFORMED_ZIP'
  /** A ZIP entry uses a compression method other than STORE or DEFLATE. */
  | 'UNSUPPORTED_COMPRESSION'
  /** No worksheet could be found inside the workbook. */
  | 'WORKSHEET_NOT_FOUND'
  /** The XML inside an .xlsx part could not be scanned. */
  | 'MALFORMED_XML'
  /** A cell holds a formula (`<f>`) with no cached `<v>` result. */
  | 'FORMULA_WITHOUT_VALUE'
  /** CSV bytes are not valid UTF-8 (e.g. a Windows-1252 export). */
  | 'INVALID_ENCODING'
  /** The running platform cannot DEFLATE-decompress (no Compression Streams). */
  | 'DECOMPRESSION_UNAVAILABLE';

/**
 * The single error type every reader/writer throws. Carries a machine-readable
 * {@link SpreadsheetErrorCode} so a consumer can map it to a translated,
 * user-facing message without string-matching the English `.message`.
 */
export class SpreadsheetError extends Error {
  readonly code: SpreadsheetErrorCode;
  constructor(code: SpreadsheetErrorCode, message: string) {
    super(message);
    this.name = 'SpreadsheetError';
    this.code = code;
  }
}

/**
 * A single parsed worksheet at the lowest level: a dense, row-major grid of
 * display-value strings (dates already rendered ISO `YYYY-MM-DD`, numbers as
 * their raw numeric text). The header row, if any, is `grid[0]`.
 */
export interface RawSheet {
  /** The worksheet name as it appears in the workbook (or a caller-chosen name for a single CSV file). */
  name: string;
  /** Dense, row-major, 0-indexed grid of display-value strings. */
  grid: string[][];
  /**
   * Cell references (A1 notation, e.g. `"B2"`) whose SOURCE cell was a raw
   * number — not text, not a date. A consumer can warn when one of these
   * lands in a column that must preserve leading zeros or an exact string
   * (account numbers, national IDs), where a spreadsheet app has silently
   * turned the value into a float or scientific-notation number.
   */
  numericCells: ReadonlySet<string>;
}

/** A whole workbook of {@link RawSheet}s, in workbook order. */
export interface RawWorkbook {
  sheets: RawSheet[];
}

/**
 * A worksheet after header extraction: `headers` is the first grid row,
 * `rows` are the remaining rows keyed by header. `kind` is a caller-assigned
 * logical identifier (sheet detection by name/header match is the consumer's
 * concern — this package only carries the value through); it defaults to
 * `sourceName` when the caller does not reclassify the sheet.
 */
export interface ParsedSheet {
  kind: string;
  sourceName: string;
  headers: string[];
  rows: Record<string, string>[];
}

/** A whole workbook of {@link ParsedSheet}s. */
export interface ParsedWorkbook {
  sheets: ParsedSheet[];
}
